import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Plus, Download, Send, Trash2, Search, CheckCircle, Clock,
  IndianRupee, Receipt as ReceiptIcon,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import TenantCombobox from '../ui/TenantCombobox';
import { toast } from '../../hooks/use-toast';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const todayMonth = () => new Date().toISOString().slice(0, 7);

const Invoices = () => {
  const { token } = useAuth();
  const [tab, setTab] = useState('all');   // all | invoices | receipts
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [paidFilter, setPaidFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [sendingWA, setSendingWA] = useState(null);

  const h = { headers: { Authorization: `Bearer ${token}` } };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const qs = [
        tab !== 'all' && `type=${tab === 'invoices' ? 'invoice' : 'receipt'}`,
        paidFilter === 'paid' && 'paid=true',
        paidFilter === 'unpaid' && 'paid=false',
        monthFilter && `month=${monthFilter}`,
      ].filter(Boolean).join('&');
      const [iRes, sRes] = await Promise.all([
        axios.get(`${API}/invoices${qs ? '?' + qs : ''}`, h),
        axios.get(`${API}/invoices/summary`, h),
      ]);
      setItems(iRes.data);
      setSummary(sRes.data);
    } catch {
      toast({ title: 'Failed to load invoices', variant: 'destructive' });
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, tab, paidFilter, monthFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openPdf = async (d) => {
    try {
      const res = await axios.get(`${API}/invoices/${d.id}/pdf`, { ...h, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast({ title: 'Failed to load PDF', variant: 'destructive' });
    }
  };

  const sendWA = async (d) => {
    setSendingWA(d.id);
    try {
      const res = await axios.post(`${API}/invoices/${d.id}/send-whatsapp`, {}, h);
      if (res.data?.sent) {
        toast({ title: 'Sent on WhatsApp', description: `${d.type === 'receipt' ? 'Receipt' : 'Invoice'} ${d.number} sent to ${d.tenant_name}.` });
      } else {
        toast({ title: 'Send failed', description: res.data?.error || 'Try again', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Send failed', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setSendingWA(null); }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/invoices/${deleting.id}`, h);
      toast({ title: 'Deleted' });
      setDeleting(null); fetchData();
    } catch (e) {
      toast({ title: 'Delete failed', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setDeleteLoading(false); }
  };

  const filtered = items.filter(d => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (d.number || '').toLowerCase().includes(s)
      || (d.tenant_name || '').toLowerCase().includes(s)
      || (d.property_name || '').toLowerCase().includes(s)
      || (d.month || '').toLowerCase().includes(s);
  });

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="invoices-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="text-indigo-600" /> Invoices & Receipts
          </h1>
          <p className="text-gray-600 mt-1">Branded PDF bills & payment receipts — auto-generated on every payment.</p>
        </div>
        <Button
          data-testid="new-invoice-btn"
          onClick={() => setShowForm(true)}
          className="bg-indigo-600 hover:bg-indigo-700"
        >
          <Plus size={16} className="mr-2" /> New Invoice
        </Button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <Card className="p-4 border-l-4 border-l-indigo-500">
            <p className="text-xs text-gray-500">Total Docs</p>
            <p className="text-2xl font-bold text-gray-900 mt-0.5">{summary.total}</p>
          </Card>
          <Card className="p-4 border-l-4 border-l-green-500">
            <p className="text-xs text-gray-500">Receipts</p>
            <p className="text-2xl font-bold text-green-700 mt-0.5">{summary.receipts}</p>
          </Card>
          <Card className="p-4 border-l-4 border-l-amber-500">
            <p className="text-xs text-gray-500">Unpaid Invoices</p>
            <p className="text-2xl font-bold text-amber-700 mt-0.5">{summary.invoices_unpaid}</p>
          </Card>
          <Card className="p-4 border-l-4 border-l-blue-500">
            <p className="text-xs text-gray-500">Collected</p>
            <p className="text-xl font-bold text-blue-700 mt-0.5">₹{(summary.total_collected || 0).toLocaleString()}</p>
          </Card>
          <Card className="p-4 border-l-4 border-l-red-500">
            <p className="text-xs text-gray-500">Pending</p>
            <p className="text-xl font-bold text-red-700 mt-0.5">₹{(summary.total_pending || 0).toLocaleString()}</p>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b">
        {[['all','All'], ['invoices','Invoices'], ['receipts','Receipts']].map(([v, l]) => (
          <button
            key={v}
            data-testid={`invoices-tab-${v}`}
            onClick={() => setTab(v)}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 -mb-px transition ${
              tab === v ? 'border-indigo-500 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="invoice-search"
            placeholder="Search by #, tenant, property, month..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <input
          data-testid="invoice-month-filter"
          type="month"
          value={monthFilter}
          onChange={e => setMonthFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        />
        <select
          data-testid="invoice-paid-filter"
          value={paidFilter}
          onChange={e => setPaidFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="">All</option>
          <option value="paid">Paid</option>
          <option value="unpaid">Unpaid</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center" data-testid="invoices-empty">
          <FileText size={56} className="mx-auto text-gray-300 mb-3" />
          <h3 className="text-xl font-semibold mb-2">No {tab === 'all' ? 'documents' : tab} yet</h3>
          <p className="text-gray-600 mb-4">
            {tab === 'receipts'
              ? 'Receipts are auto-generated when you record a payment.'
              : 'Create your first invoice to bill a tenant for a specific month.'}
          </p>
          {tab !== 'receipts' && (
            <Button onClick={() => setShowForm(true)} className="bg-indigo-600 hover:bg-indigo-700">
              <Plus size={16} className="mr-2" /> New Invoice
            </Button>
          )}
        </Card>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
          <table className="w-full" data-testid="invoices-table">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="p-3 text-left text-xs font-semibold text-gray-700 uppercase">#</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-700 uppercase">Type</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-700 uppercase">Tenant / Property</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-700 uppercase">Month</th>
                <th className="p-3 text-right text-xs font-semibold text-gray-700 uppercase">Amount</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-700 uppercase">Status</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-700 uppercase">Issued / Due</th>
                <th className="p-3 text-right text-xs font-semibold text-gray-700 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(d => {
                const isReceipt = d.type === 'receipt';
                return (
                  <tr key={d.id} className="border-b hover:bg-gray-50" data-testid={`invoice-row-${d.id}`}>
                    <td className="p-3">
                      <code className="text-xs font-mono font-medium text-gray-800">{d.number}</code>
                    </td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        isReceipt ? 'bg-green-100 text-green-700' : 'bg-indigo-100 text-indigo-700'
                      }`}>
                        {isReceipt ? <ReceiptIcon size={11} /> : <FileText size={11} />}
                        {isReceipt ? 'Receipt' : 'Invoice'}
                      </span>
                    </td>
                    <td className="p-3 text-sm">
                      <p className="font-medium text-gray-900">{d.tenant_name || '—'}</p>
                      <p className="text-xs text-gray-500">{d.property_name}{d.room_number ? ` · Rm ${d.room_number}` : ''}</p>
                    </td>
                    <td className="p-3 text-sm">
                      <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">{d.month}</span>
                    </td>
                    <td className="p-3 text-right text-sm font-semibold tabular-nums text-gray-900">
                      <span className="inline-flex items-center"><IndianRupee size={12} />{(d.amount || 0).toLocaleString()}</span>
                    </td>
                    <td className="p-3">
                      {d.paid ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                          <CheckCircle size={11} /> Paid
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-xs font-medium">
                          <Clock size={11} /> Unpaid
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-xs text-gray-600">
                      <p>{d.issued_date}</p>
                      {d.due_date && !d.paid && <p className="text-amber-700">Due: {d.due_date}</p>}
                    </td>
                    <td className="p-3">
                      <div className="flex gap-1 justify-end">
                        <button
                          data-testid={`invoice-pdf-${d.id}`}
                          onClick={() => openPdf(d)}
                          className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded"
                          title="Open PDF"
                        ><Download size={14} /></button>
                        <button
                          data-testid={`invoice-wa-${d.id}`}
                          onClick={() => sendWA(d)}
                          disabled={sendingWA === d.id}
                          className="p-1.5 text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
                          title="Send on WhatsApp"
                        ><Send size={14} /></button>
                        <button
                          data-testid={`invoice-delete-${d.id}`}
                          onClick={() => setDeleting(d)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        ><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <NewInvoiceModal
          open={showForm}
          onClose={() => setShowForm(false)}
          onSuccess={() => { setShowForm(false); fetchData(); }}
          token={token}
        />
      )}

      <DeleteConfirmationModal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={`Delete ${deleting?.type === 'receipt' ? 'Receipt' : 'Invoice'}`}
        message="This cannot be undone. Associated payment (if any) is NOT deleted."
        itemName={deleting ? `${deleting.number} · ${deleting.tenant_name}` : ''}
        loading={deleteLoading}
      />
    </div>
  );
};

// ---------- New Invoice Modal ----------
const NewInvoiceModal = ({ open, onClose, onSuccess, token }) => {
  const [tenants, setTenants] = useState([]);
  const [form, setForm] = useState({
    tenant_id: '', month: todayMonth(), amount: '', due_date: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);

  const h = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/tenants`, h);
        setTenants((r.data || []).filter(t => t.status === 'active'));
      } catch {
        setTenants([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!form.tenant_id) { setSelectedTenant(null); return; }
    const t = tenants.find(x => x.id === form.tenant_id);
    setSelectedTenant(t || null);
    if (t) {
      setForm(prev => ({
        ...prev,
        amount: prev.amount || t.rent_amount || '',
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.tenant_id, tenants]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/invoices`, {
        tenant_id: form.tenant_id,
        month: form.month,
        amount: parseFloat(form.amount) || 0,
        due_date: form.due_date || null,
        notes: form.notes || null,
      }, h);
      toast({ title: 'Invoice created', description: `PDF ready for ${form.month}.` });
      onSuccess();
    } catch (e) {
      toast({ title: 'Create failed', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText size={20} className="text-indigo-600" /> New Invoice
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="invoice-form">
          <div>
            <Label>Tenant *</Label>
            <TenantCombobox
              tenants={tenants}
              value={form.tenant_id}
              onChange={(id) => setForm({ ...form, tenant_id: id })}
              required
              testId="invoice-tenant"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Billing Month *</Label>
              <Input
                data-testid="invoice-month"
                type="month"
                value={form.month}
                onChange={e => setForm({ ...form, month: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Amount (₹) *</Label>
              <Input
                data-testid="invoice-amount"
                type="number"
                min="0"
                value={form.amount}
                onChange={e => setForm({ ...form, amount: e.target.value })}
                required
              />
            </div>
          </div>

          <div>
            <Label>Due Date (Optional)</Label>
            <Input
              data-testid="invoice-due"
              type="date"
              value={form.due_date}
              onChange={e => setForm({ ...form, due_date: e.target.value })}
            />
            {selectedTenant?.rent_due_day && !form.due_date && (
              <p className="text-xs text-gray-500 mt-1">
                Will default to {selectedTenant.rent_due_day}th of {form.month} from tenant's rent due day.
              </p>
            )}
          </div>

          <div>
            <Label>Notes (Optional)</Label>
            <textarea
              data-testid="invoice-notes"
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="e.g., Includes electricity, maintenance charges..."
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              data-testid="invoice-submit"
              type="submit"
              disabled={saving}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700"
            >
              {saving ? 'Creating...' : 'Create Invoice'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default Invoices;
